import { Box, Grid, Typography } from "@drone-js/core";
import { SEO } from "apps/commons/components/SEO";
import { propertyCategoryBinder } from "libs/analytics/binders/propertySelection/propertyCategory";
import { useAnalytics } from "libs/analytics/useAnalytics";
import { type FunctionComponent, useEffect } from "react";
import { useSearchParams } from "react-router";
import { Footer } from "../Footer";
import { useListNewPropertySourceState } from "../headerAtom";
import { ListNewPropertySource } from "../headerConstants";
import { useOnboardingTypeSelectionHandler } from "../importMethod/useOnboardingTypeSelectionHandler";
import { usePropertySelectionViewModelValue } from "../propertySelectionViewModelAtom";
import { useKeyDownHandler } from "../useKeyDownHandler";
import { CardSelector, type CardSelectorProps } from "./CardSelector";
import { NotSelectedAlert } from "./NotSelectedAlert";
import { UnknownErrorToast } from "./UnknownErrorToast";
import { ValidationError, propertyCategoryMapper } from "./constants";
import { usePropertyCategoryValue } from "./propertyCategoryAtom";
import { usePropertyCategoryHandler } from "./usePropertyCategoryHandler";

export const PropertyCategoryPage: FunctionComponent = () => {
    const {
        navigationCms: { back, next },
        importMethodCms: { exit },
        propertyCategoryCms: {
            metaTitle,
            metaDescription,
            whatTypeOfPropertyAreYouListing,
            homeTypeProperty,
            standaloneUnitsRentedDesktop,
            standaloneUnitsRentedMobile,
            hotelTypeProperty,
            multipleRentedUnitsDesktop,
            multipleRentedUnitsMobile,
            uniqueTypeProperty,
            specialAccommodationsDesktop,
            specialAccommodationsMobile,
        },
        isDisableAirbnbImport,
    } = usePropertySelectionViewModelValue();
    const { error, onNext, onBack } = usePropertyCategoryHandler();
    const { onExit } = useOnboardingTypeSelectionHandler();
    const selectedPropertyCategory = usePropertyCategoryValue();
    const [, setListNewPropertySourceState] = useListNewPropertySourceState();
    const [searchParams] = useSearchParams();
    const source = searchParams.get("source");

    const cards: CardSelectorProps[] = [
        {
            type: propertyCategoryMapper.Home,
            title: homeTypeProperty,
            subtitle: standaloneUnitsRentedDesktop,
            subtitleMobile: standaloneUnitsRentedMobile,
            inactiveIcon: "line.symbol.home",
            activeIcon: "fill.symbol.home",
            dataElementName: "property-category-home-card",
        },
        {
            type: propertyCategoryMapper.Hotel,
            title: hotelTypeProperty,
            subtitle: multipleRentedUnitsDesktop,
            subtitleMobile: multipleRentedUnitsMobile,
            inactiveIcon: "line.accommodation.hotel",
            activeIcon: "fill.accommodation.hotel",
            dataElementName: "property-category-hotel-card",
        },
        {
            type: propertyCategoryMapper.Unique,
            title: uniqueTypeProperty,
            subtitle: specialAccommodationsDesktop,
            subtitleMobile: specialAccommodationsMobile,
            inactiveIcon: "line.activity.tent",
            activeIcon: "fill.activity.tent",
            dataElementName: "property-category-unique-card",
        },
    ];

    useKeyDownHandler(
        (e) => {
            if (e.code === "Enter") onNext();
        },
        [onNext],
    );

    useEffect(() => {
        if (!source) setListNewPropertySourceState(ListNewPropertySource.NewUser);
        else setListNewPropertySourceState(+source);
    }, []);

    useAnalytics(propertyCategoryBinder);

    return (
        <Box py="24" gap="24" display="flex" flexDirection="column" qM={{ py: "32" }}>
            <SEO title={metaTitle} description={metaDescription} />
            <Typography variant="heading-xl" textColor="base.default">
                {whatTypeOfPropertyAreYouListing}
            </Typography>
            {error === ValidationError.NotSelected && selectedPropertyCategory === undefined && <NotSelectedAlert />}
            <Grid spacing="16">
                {cards.map((card) => (
                    <CardSelector key={card.type} {...card} />
                ))}
            </Grid>
            {error === ValidationError.Unknown && <UnknownErrorToast />}
            <Footer
                secondary={
                    isDisableAirbnbImport
                        ? {
                              label: exit,
                              dataElementName: "exit-button",
                          }
                        : {
                              label: back,
                              dataElementName: "back",
                          }
                }
                primary={{
                    label: next,
                    dataElementName: "next",
                }}
                onSecondary={isDisableAirbnbImport ? onExit : onBack}
                onPrimary={onNext}
            />
        </Box>
    );
};
